import { useEffect, useState } from "react";
import { AddProductModal, ButtonFrame, Card, CardSearch, InputField, PageBodyHeader, Table } from "../../component";
import ModalParent from "../../component/modal/ModalParent";
import { generalObj } from "../../constant/object_types";
import { useSelector } from "react-redux";
import { RootState } from "../../redux";
import { checkStoreByStoreIdUserId, createOrderRequest, dateToSystemTz, exportOrderRequestPdf, fetchOrderRequest, fetchOrderRequestById, fetchProductList, fulfillOrderRequest } from "../../action/generalFunc";
import { useOutletContext, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { IoAdd, IoChevronDown, IoTrashBinOutline } from "react-icons/io5";
import { PdfSvg } from "../../style/iconStyle";

type RequestRow = {
    id: number,
    product: string,
    product_obj: generalObj | null,
    requested_quantity: string,
    unit: string
};

type FulfillmentRow = {
    id: number | string,
    product_name: string,
    unit_label: string,
    requested_quantity: number | string,
    fulfilled_quantity: string
};

const statusBadgeColor = (status:string) => {
    switch (status.toLowerCase()) {
        case "approved":
        case "fulfilled": 
            return { backgroundColor: "#E4F7EA", color: "#00983A" };
        case "rejected": return { backgroundColor: "#FCE4E4", color: "#EB212F" };
        default: return { backgroundColor: "#FFF3D6", color: "#B5820B" };
    }
};

// const orderStatusOptions = ["Pending", "Approved", "Rejected"];

const StaffOrderRequest = () => {
    const { id, store_id } = useParams();
    const account = useSelector((state:RootState) => state.account);
    const { setModalLoading }: { setModalLoading:Function } = useOutletContext();

    const [requestRows, setRequestRows] = useState<RequestRow[]>([
        { id: 1, product: "", product_obj: null, requested_quantity: "", unit: "" }
    ]);
    const [productList, setProductList] = useState<generalObj[]>([]);
    const [orderList, setOrderList] = useState<generalObj[]>([]);
    const [searchText, setSearchText] = useState<string>("");
    const [openRequestModal, setOpenRequestModal] = useState<boolean>(false);
    const [activeRowId, setActiveRowId] = useState<number | null>(null);
    const [storeName, setStoreName] = useState<string>("");
    const [vendorName, setVendorName] = useState<string>("");
    const [viewOrderRequest, setViewOrderRequest] = useState<generalObj | null>(null);
    const [openViewModal, setOpenViewModal] = useState<boolean>(false);
    const [fulfillOrderId, setFulfillOrderId] = useState<number | string | null>(null);
    const [fulfillmentDate, setFulfillmentDate] = useState<string>("");
    const [fulfillmentRows, setFulfillmentRows] = useState<FulfillmentRow[]>([]);
    const [openFulfillModal, setOpenFulfillModal] = useState<boolean>(false);
    const [openCreateProductModal, setOpenCreateProductModal] = useState<boolean>(false);
    const [createProductInitialName, setCreateProductInitialName] = useState<string>("");
    const [targetRowIdForProduct, setTargetRowIdForProduct] = useState<number | null>(null);

    useEffect(() => {
        if (!account || !account.isAuthenticated || !id || !store_id) return;

        const loadForm = async () => {
            setModalLoading(true);
            const storeResult = await checkStoreByStoreIdUserId(account.token, store_id, id);
            if (!storeResult) {
                window.history.back();
                setModalLoading(false);
                return;
            }

            setStoreName(`${storeResult.data.store_data.name} - ${storeResult.data.store_data.organization}`);
            setVendorName(storeResult.data.vendor_data.username);
            const productResult = await fetchProductList(account.token, { vendor: id, store: store_id });
            setProductList(productResult?.data?.product || []);
            const orderResult = await fetchOrderRequest(account.token, store_id, id);
            setOrderList(orderResult || []);
            setModalLoading(false);
        };

        loadForm();
    }, [account, id, store_id, setModalLoading]);

    const updateRow = (rowId:number, key:string, value:any) => {
        setRequestRows((previousRows) => previousRows.map((row) => row.id === rowId ? { ...row, [key]: value } : row));
    };

    const selectProduct = (rowId:number, product:generalObj) => {
        if (requestRows.some((row) => row.product_obj?.id === product.id && row.id !== rowId)) {
            toast("This product has already been selected");
            return;
        }
        setRequestRows((previousRows) => previousRows.map((row) => row.id === rowId ? { ...row, product: "", product_obj: product } : row));
        setActiveRowId(null);
    };

    const addRequestRow = () => {
        setRequestRows((previousRows) => [...previousRows, {
            id: Date.now(),
            product: "",
            product_obj: null,
            requested_quantity: "",
            unit: ""
        }]);
    };

    const removeRequestRow = (rowId:number) => {
        setRequestRows((previousRows) => previousRows.filter((row) => row.id !== rowId));
        setActiveRowId(null);
    };

    const handleOpenCreateProductModal = (rowId:number, initialName:string = "") => {
        setTargetRowIdForProduct(rowId);
        setCreateProductInitialName(initialName);
        setOpenCreateProductModal(true);
        setActiveRowId(null);
    };

    const handleProductCreated = async (newProductData:generalObj) => {
        if (!account || !id || !store_id) return;
        setModalLoading(true);
        const productResult = await fetchProductList(account.token, { vendor: id, store: store_id });
        const products = productResult?.data?.product || [];
        setProductList(products);

        let matchedProduct = null;
        if (newProductData?.id) {
            matchedProduct = products.find((p:generalObj) => p.id === newProductData.id);
        }
        if (!matchedProduct && newProductData?.name) {
            matchedProduct = products.find((p:generalObj) => p.name?.toLowerCase() === newProductData.name?.toLowerCase());
        }
        if (!matchedProduct && products.length > 0) {
            matchedProduct = products[products.length - 1];
        }

        if (matchedProduct && targetRowIdForProduct !== null) {
            selectProduct(targetRowIdForProduct, matchedProduct);
        }
        setModalLoading(false);
    };

    const openFulfillmentModal = async (orderRequestId:number|string) => {
        if (!account || !account.isAuthenticated) return;
        setModalLoading(true);
        const orderRequestResult = await fetchOrderRequestById(account.token, orderRequestId);
        if (orderRequestResult) {
            setFulfillOrderId(orderRequestId);
            setFulfillmentDate(new Date().toISOString().slice(0, 10));
            setFulfillmentRows((orderRequestResult.items || []).map((item:generalObj, itemIndex:number) => ({
                id: item.id ?? itemIndex,
                product_name: item.product?.name || item.product_name || item.product || "-",
                unit_label: item.unit?.label || item.unit_name || item.unit || "-",
                requested_quantity: item.requested_quantity ?? item.quantity ?? "-",
                fulfilled_quantity: String(item.requested_quantity ?? item.quantity ?? "")
            })));
            setOpenFulfillModal(true);
        }
        setModalLoading(false);
    };

    const updateFulfillmentRow = (rowId:number|string, value:string) => {
        setFulfillmentRows((previousRows) => previousRows.map((row) => row.id === rowId ? { ...row, fulfilled_quantity: value } : row));
    };

    const submitFulfillment = async () => {
        if (!account || !account.isAuthenticated || fulfillOrderId === null) return;
        if (fulfillmentDate === "") {
            toast("Fulfillment date is required");
            return;
        }
        for (const row of fulfillmentRows) {
            if (row.fulfilled_quantity === "") {
                toast("Fulfilled quantity is required for every product");
                return;
            }
            const fulfilledQuantity = Number(row.fulfilled_quantity);
            if (!Number.isInteger(fulfilledQuantity) || fulfilledQuantity < 0) {
                toast("Fulfilled quantity must be a whole number");
                return;
            }
        }

        setModalLoading(true);
        const result = await fulfillOrderRequest(account.token, fulfillOrderId, {
            items: fulfillmentRows.map((row) => ({
                id: row.id,
                received_quantity: Number(row.fulfilled_quantity)
            }))
        });

        if (result) {
            toast(result);
            const orderResult = await fetchOrderRequest(account.token, store_id, id);
            setOrderList(orderResult || []);
            setOpenFulfillModal(false);
        }
        setModalLoading(false);
    };

    const viewOrderRequestDetail = async (orderRequestId:number|string) => {
        if (!account || !account.isAuthenticated) return;
        setModalLoading(true);
        const orderRequestResult = await fetchOrderRequestById(account.token, orderRequestId);
        if (orderRequestResult) {
            setViewOrderRequest(orderRequestResult);
            setOpenViewModal(true);
        }
        setModalLoading(false);
    };

    const exportOrderRequest = async (orderRequestId:number|string) => {
        if (!account || !account.isAuthenticated) return;
        setModalLoading(true);
        const result = await exportOrderRequestPdf(account.token, orderRequestId);
        if (result && typeof(result) === "object") {
            const aElement = document.createElement('a');
            aElement.setAttribute('download', `order_request_${orderRequestId}.pdf`);
            const href = URL.createObjectURL(result.data);
            aElement.href = href;
            aElement.setAttribute('target', '_blank');
            aElement.click();
            URL.revokeObjectURL(href);
            toast("Order request exported");
        }
        setModalLoading(false);
    };

    const submitRequest = async () => {
        if (!account || !account.isAuthenticated || !id || !store_id) return;
        for (const row of requestRows) {
            if (!row.product_obj) {
                toast("Kindly select a product for every request");
                return;
            }
            if (row.requested_quantity === "") {
                toast("Requested quantity is required for every product");
                return;
            }
            if (row.unit.trim() === "") {
                toast("Quantity unit is required for every product");
                return;
            }
            const requestedQuantity = Number(row.requested_quantity);
            if (!Number.isInteger(requestedQuantity) || requestedQuantity < 0) {
                toast("Requested quantity must be a whole number");
                return;
            }
        }

        setModalLoading(true);
        const result = await createOrderRequest(account.token, {
            vendor: id,
            store: store_id,
            items: requestRows.map((row) => ({
                product: row.product_obj?.id,
                quantity: Number(row.requested_quantity),
                unit: row.unit.trim()
            }))
        });

        if (result) {
            toast(result);
            const orderResult = await fetchOrderRequest(account.token, store_id, id);
            setOrderList(orderResult || []);
            setRequestRows([{ id: Date.now(), product: "", product_obj: null, requested_quantity: "", unit: "" }]);
            setOpenRequestModal(false);
        }
        setModalLoading(false);
    };

    return (
        <>
            <PageBodyHeader title="Order Request" desc="Request products for your store"
                inlineChild={<ButtonFrame btn_text="Back" btn_style={{padding:"8px 24px", borderRadius:"8px", border:"1px solid #808280",  backgroundColor:"#808280", color:"#FFF", fontSize:"14px", fontWeight:"600", letterSpacing:"-0.4px"}} onPress={() => window.history.back()} />}
            >
                <ButtonFrame btn_text="New Order Request" btn_style={{padding:"10px 18px", borderRadius:"8px", backgroundColor:"#1159AB", color:"#FFF", fontSize:"14px", fontWeight:"600"}} onPress={() => setOpenRequestModal(true)} />
            </PageBodyHeader>
            {/* <h3 style={{marginBottom:"18px", textAlign:"center"}}>{`Create Order Request for ${vendorName.toLocaleUpperCase()} in ${storeName} store`}</h3> */}

            <ModalParent visible={openRequestModal}>
                <div className="ViewContainer">
                    <div className="input__pop" style={{maxWidth:"1000px", width:"90vw", maxHeight:"90vh", display:"flex", flexDirection:"column", padding:"20px", overflow:"hidden"}}>
                        <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"16px", flexShrink:0}}>
                            <h4 style={{fontSize:"16px", margin:0, wordBreak:"break-word"}}>{`New Order Request for ${vendorName.toLocaleUpperCase()} in ${storeName} store`}</h4>
                            <button type="button" title="Close" onClick={() => setOpenRequestModal(false)} style={{fontSize:"24px", padding:"0 8px", background:"transparent", border:"none", cursor:"pointer"}}>&times;</button>
                        </div>
                        <Card color="#676967" containerCardStyle={{overflowX:"auto", overflowY:"auto", maxHeight:"calc(90vh - 100px)", padding:"15px"}}>
                            <div style={{width:"100%"}}>
                                <table className="tableComponent" style={{width:"100%", minWidth:"600px"}}>
                                    <thead><tr><th className="headerText white">Product</th><th className="headerText white">Quantity</th><th className="headerText white">Unit</th><th className="headerText white">Action</th></tr></thead>
                                    <tbody>
                                        {requestRows.map((row, index) => (
                                            <tr key={row.id}>
                                                <td style={{minWidth:"240px"}}>
                                                    <div className="searchdrop" style={{position:"relative"}}>
                                                        {row.product_obj ? (
                                                            <ButtonFrame btn_text={`${row.product_obj.name} - ${row.product_obj.size}${row.product_obj.unit || ""}`} btn_style={{padding:"8px 16px", maxHeight:"40px", borderRadius:"8px", border:"1px solid #808280", color:"#808280", fontSize:"14px", fontWeight:"600", textAlign:"left", width:"100%"}} onPress={() => updateRow(row.id, "product_obj", null)} />
                                                        ) : (
                                                            <>
                                                                <InputField label={`Product ${index + 1} *`} inputProp={{value:row.product, placeholder:"Search connected products", onFocus:() => setActiveRowId(row.id)}} onTextChange={(value) => { updateRow(row.id, "product", value); setActiveRowId(row.id); }} containerStyle={{width:"100%"}} isIconRight={<IoChevronDown size={14} color="#B2B5B2" />} />
                                                                <div className={`inputDropDown ${activeRowId === row.id ? "active" : ""}`} style={{top:"60px"}}><ul>
                                                                    {productList.filter((product) => !requestRows.some((selectedRow) => selectedRow.product_obj?.id === product.id && selectedRow.id !== row.id)).filter((product) => `${product.name} ${product.description || ""} ${product.size} ${product.unit || ""}`.toLowerCase().includes(row.product.toLowerCase())).map((product:generalObj, productIndex:number) => <li key={product.id || productIndex}><button type="button" onClick={() => selectProduct(row.id, product)}>{product.name} - {product.size}{product.unit || ""}</button></li>)}
                                                                    {productList.filter((product) => !requestRows.some((selectedRow) => selectedRow.product_obj?.id === product.id && selectedRow.id !== row.id)).filter((product) => `${product.name} ${product.description || ""} ${product.size} ${product.unit || ""}`.toLowerCase().includes(row.product.toLowerCase())).length === 0 && <li><button type="button" style={{color:"#888", cursor:"default"}}>No matching products</button></li>}
                                                                    <li style={{borderTop:"1px solid #E0E0E0", backgroundColor:"#F0F5FF"}}>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => handleOpenCreateProductModal(row.id, row.product)}
                                                                            style={{color:"#1159AB", fontWeight:"600", display:"flex", alignItems:"center", gap:"6px", padding:"9px 10px", width:"100%", textAlign:"left"}}
                                                                        >
                                                                            <IoAdd size={16} color="#1159AB" />
                                                                            {row.product.trim() ? `Create "${row.product.trim()}" on behalf of admin` : "Create new product on behalf of admin"}
                                                                        </button>
                                                                    </li>
                                                                </ul></div>
                                                            </>
                                                        )}
                                                    </div>
                                                </td>
                                                <td style={{minWidth:"110px"}}><InputField label="" inputProp={{value:row.requested_quantity, placeholder:"Quantity", type:"number", min:"0", step:"1"}} onTextChange={(value) => updateRow(row.id, "requested_quantity", value)} containerStyle={{width:"100%"}} /></td>
                                                <td style={{minWidth:"120px"}}><InputField label="" inputProp={{value:row.unit, placeholder:"Enter unit"}} onTextChange={(value) => updateRow(row.id, "unit", value)} containerStyle={{width:"100%"}} /></td>
                                                <td style={{textAlign:"center", verticalAlign:"middle"}}>{requestRows.length > 1 && <button type="button" title="Remove product" style={{padding:"8px 12px", background:"transparent", border:"none", cursor:"pointer"}} onClick={() => removeRequestRow(row.id)}><IoTrashBinOutline size={18} color="#EB212F" /></button>}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>

                                <div style={{marginTop:"24px", display:"flex", alignItems:"center", gap:"12px", flexWrap:"wrap"}}>
                                    <button type="button" className="product_add_button" style={{display:"flex", alignItems:"center", gap:"6px", backgroundColor:"#1159AB", color:"#FFF", padding:"8px 14px", borderRadius:"5px", border:"none", cursor:"pointer"}} onClick={addRequestRow}>
                                        <IoAdd size={16} /> Add Product
                                    </button>
                                    <button type="button" style={{display:"flex", alignItems:"center", gap:"6px", backgroundColor:"#EBF3FC", color:"#1159AB", border:"1px solid #1159AB", padding:"8px 14px", borderRadius:"5px", fontSize:"14px", fontWeight:"600", cursor:"pointer"}} onClick={() => handleOpenCreateProductModal(activeRowId || requestRows[requestRows.length - 1]?.id || 1, "")}>
                                        <IoAdd size={16} color="#1159AB" /> Create New Product
                                    </button>
                                </div>

                                <div style={{marginTop:"32px", display:"flex", alignItems:"center", gap:"16px", flexWrap:"wrap"}}>
                                    <ButtonFrame btn_text="Submit Request" btn_style={{padding:"12px 24px", borderRadius:"8px", backgroundColor:"#00983A", color:"#FFF", fontSize:"14px", fontWeight:"600"}} onPress={submitRequest} />
                                    <ButtonFrame btn_text="Cancel" btn_style={{padding:"12px 24px", borderRadius:"8px", border:"1px solid #808280", color:"#808280", fontSize:"14px", fontWeight:"600"}} onPress={() => setOpenRequestModal(false)} />
                                </div>
                            </div>
                        </Card>
                    </div>
                </div>
            </ModalParent>

            <div style={{marginTop:"30px"}}>
                <Table table_head={["Order Request #", "Products", "Date", "Status", "Action"]} th_class="white" color="#1159AB" thColor="#1159AB" tableSearch={<CardSearch value={searchText} onTextChange={(value) => setSearchText(value)} />} table_title={`Order requests for ${vendorName.toUpperCase()} in ${storeName}`}>
                    <>
                        {orderList.map((order:generalObj, orderIndex:number) => ({
                            key: order.id ?? orderIndex,
                            request_number: order.request_number || order.order_number || order.order_request_number || order.id || orderIndex + 1,
                            product_count: (order.items || []).length,
                            date: dateToSystemTz(order.created_at || order.order_made_date || order.date),
                            status: order.status || order.order_status || "Pending"
                        })).filter((row) => `${row.request_number} ${row.status}`.toLowerCase().includes(searchText.toLowerCase())).map((row) => (
                            <tr key={row.key}>
                                <td>#{row.request_number}</td>
                                <td>{row.product_count}</td>
                                <td>{row.date}</td>
                                <td><span style={{padding:"4px 12px", borderRadius:"12px", fontSize:"12px", fontWeight:"600", ...statusBadgeColor(row.status)}}>{row.status}</span></td>
                                <td>
                                    <div style={{display:"flex", alignItems:"center", gap:"10px"}}>
                                        {row.status.toLowerCase() === "fulfilled" ? null : <button type="button" onClick={() => openFulfillmentModal(row.key)} style={{padding:"6px 12px", borderRadius:"6px", border:"1px solid #00983A", color:"#00983A", fontWeight:"600"}}>Fulfill</button>}
                                        <button type="button" onClick={() => viewOrderRequestDetail(row.key)} style={{padding:"6px 12px", borderRadius:"6px", border:"1px solid #1159AB", color:"#1159AB", fontWeight:"600"}}>View</button>
                                        <button type="button" onClick={() => exportOrderRequest(row.key)} style={{width:"24px", height:"24px", fontWeight:"600"}}>
                                            <div style={{width:"24px", height:"24px", display:"flex", alignItems:"center", justifyContent:"center"}}>
                                                <PdfSvg />
                                            </div>
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </>
                </Table>
            </div>

            <ModalParent visible={openViewModal}>
                <div className="ViewContainer">
                    <div className="input__pop" style={{maxWidth:"600px", width:"90vw"}}>
                        <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"24px"}}>
                            <h4>{`Order Request #${viewOrderRequest?.request_number || viewOrderRequest?.order_number || viewOrderRequest?.id || ""}`}</h4>
                            <button type="button" title="Close" onClick={() => setOpenViewModal(false)} style={{fontSize:"22px", padding:"4px 10px"}}>&times;</button>
                        </div>
                        <Card color="#676967" containerCardStyle={{overflow:"auto"}}>
                            <div style={{padding:"20px"}}>
                                <div style={{display:"flex", justifyContent:"space-between", marginBottom:"16px"}}>
                                    <span>{`Date: ${dateToSystemTz(viewOrderRequest?.created_at || viewOrderRequest?.order_made_date || viewOrderRequest?.date)}`}</span>
                                    <span style={{padding:"4px 12px", borderRadius:"12px", fontSize:"12px", fontWeight:"600", ...statusBadgeColor(viewOrderRequest?.status || viewOrderRequest?.order_status || "Pending")}}>{viewOrderRequest?.status || viewOrderRequest?.order_status || "Pending"}</span>
                                </div>
                                <div style={{marginBottom:"16px"}}>
                                    <span>{`Fulfillment Date: ${dateToSystemTz(viewOrderRequest?.fulfillment_at || viewOrderRequest?.fulfilled_at)}`}</span>
                                </div>
                                <table className="tableComponent" style={{width:"100%"}}>
                                    <thead><tr><th className="headerText white">Product</th><th className="headerText white">Quantity</th><th className="headerText white">Unit</th><th className="headerText white">Received Qty</th></tr></thead>
                                    <tbody>
                                        {(viewOrderRequest?.items || []).map((item:generalObj, itemIndex:number) => (
                                            <tr key={item.id || itemIndex}>
                                                <td>{item.product?.name || item.product_name || item.product || "-"}</td>
                                                <td>{item.requested_quantity ?? item.quantity ?? "-"}</td>
                                                <td>{item.unit?.label || item.unit_name || item.unit || "-"}</td>
                                                <td>{item.received_quantity ?? "-"}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </Card>
                    </div>
                </div>
            </ModalParent>

            <ModalParent visible={openFulfillModal}>
                <div className="ViewContainer">
                    <div className="input__pop" style={{maxWidth:"700px", width:"90vw"}}>
                        <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"24px"}}>
                            <h4>{`Fulfill Order Request #${fulfillOrderId ?? ""}`}</h4>
                            <button type="button" title="Close" onClick={() => setOpenFulfillModal(false)} style={{fontSize:"22px", padding:"4px 10px"}}>&times;</button>
                        </div>
                        <Card color="#676967" containerCardStyle={{overflow:"auto"}}>
                            <div style={{padding:"20px"}}>
                                <InputField label={"Fulfillment Date *"} inputProp={{value:fulfillmentDate, type:"date"}} onTextChange={(value) => setFulfillmentDate(value)} containerStyle={{width:"260px", marginBottom:"20px"}} />
                                <table className="tableComponent" style={{width:"100%"}}>
                                    <thead><tr><th className="headerText white">Product</th><th className="headerText white">Requested Qty</th><th className="headerText white">Unit</th><th className="headerText white">Fulfilled Qty</th></tr></thead>
                                    <tbody>
                                        {fulfillmentRows.map((row) => (
                                            <tr key={row.id}>
                                                <td>{row.product_name}</td>
                                                <td>{row.requested_quantity}</td>
                                                <td>{row.unit_label}</td>
                                                <td><InputField label="" inputProp={{value:row.fulfilled_quantity, placeholder:"Quantity", type:"number", min:"0", step:"1"}} onTextChange={(value) => updateFulfillmentRow(row.id, value)} containerStyle={{width:"130px"}} /></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                <div style={{marginTop:"32px", display:"flex", alignItems:"center", gap:"20px"}}>
                                    <ButtonFrame btn_text="Submit Fulfillment" btn_style={{padding:"12px 24px", borderRadius:"8px", backgroundColor:"#00983A", color:"#FFF", fontSize:"14px", fontWeight:"600"}} onPress={submitFulfillment} />
                                    <ButtonFrame btn_text="Cancel" btn_style={{padding:"12px 24px", borderRadius:"8px", border:"1px solid #808280", color:"#808280", fontSize:"14px", fontWeight:"600"}} onPress={() => setOpenFulfillModal(false)} />
                                </div>
                            </div>
                        </Card>
                    </div>
                </div>
            </ModalParent>

            <AddProductModal
                visible={openCreateProductModal}
                modalClose={() => setOpenCreateProductModal(false)}
                onAdded={handleProductCreated}
                initialName={createProductInitialName}
                vendorId={id}
                storeId={store_id}
                vendorName={vendorName}
                storeName={storeName}
            />
        </>
    )
}

export default StaffOrderRequest;